/**
 * Reviews page — <relic-reviews-page> custom element.
 * Fetches shop reviews via app proxy, renders cards, filters, and load more.
 */

const REVIEWS_CACHE_VERSION = 6;
const REVIEWS_CACHE_KEY_PREFIX = "relic-reviews:all:";

if (!customElements.get("relic-reviews-page")) {
  class RelicReviewsPage extends HTMLElement {
    connectedCallback() {
      this.statusEl = this.querySelector("[data-reviews-status]");
      this.innerEl = this.querySelector("[data-reviews-inner]");
      this.gridEl = this.querySelector("[data-reviews-grid]");
      this.filtersEl = this.querySelector("[data-reviews-filters]");
      this.emptyEl = this.querySelector("[data-reviews-empty]");
      this.loadMoreWrapEl = this.querySelector("[data-reviews-load-more-wrap]");
      this.initialCount = parseInt(this.dataset.reviewsPerPage, 10) || 10;
      this.loadMoreCount = parseInt(this.dataset.loadMoreCount, 10) || 10;
      this.visibleCount = this.initialCount;
      this.starIconHtml =
        this.querySelector(
          "template[data-reviews-star-icon]",
        )?.innerHTML.trim() || "";
      this.verifiedBadgeHtml =
        this.querySelector(
          "template[data-reviews-verified-badge]",
        )?.innerHTML.trim() || "";
      this.filterCheckIconHtml =
        this.querySelector(
          "template[data-reviews-filter-check-icon]",
        )?.innerHTML.trim() || "";
      this.filterChevronHtml =
        this.querySelector(
          "template[data-reviews-filter-chevron-icon]",
        )?.innerHTML.trim() || "";
      this.filterAccordionMedia = window.matchMedia("(min-width: 1000px)");
      this.allReviews = [];
      this.seenReviewIds = new Set();
      this.reviewsFullyLoaded = false;
      this.filtersInitialized = false;
      this.activeSources = new Set();
      this.activeProducts = new Set();
      this.activeRatings = new Set();
      this.knownProductHandles = new Set();
      this.knownSourceKeys = new Set();
      this.knownRatings = new Set();
      this.productCache = {};
      this.productLoads = {};
      this.productImageWidths = [56, 112];
      this.renderGeneration = 0;

      this.filtersEl?.addEventListener("click", this.onFilterClick.bind(this));
      this.loadMoreWrapEl?.addEventListener(
        "click",
        this.onLoadMoreClick.bind(this),
      );
      this.onFilterAccordionMediaChange =
        this.syncFilterAccordionState.bind(this);
      this.filterAccordionMedia.addEventListener(
        "change",
        this.onFilterAccordionMediaChange,
      );
      this.initReviews();
    }

    getProductRoot() {
      const root =
        this.dataset.productRoot || window.Shopify?.routes?.root || "/";
      return root.endsWith("/") ? root : root + "/";
    }

    buildProductUrl(handle) {
      return (
        this.getProductRoot() + "products/" + encodeURIComponent(handle) + ".js"
      );
    }

    buildProductPageUrl(handle) {
      return this.getProductRoot() + "products/" + encodeURIComponent(handle);
    }

    normalizeImageUrl(url) {
      if (!url || typeof url !== "string") return "";
      if (url.startsWith("//")) return "https:" + url;
      return url;
    }

    resizeShopifyImageUrl(url, width) {
      const normalized = this.normalizeImageUrl(url);
      if (!normalized || !width) return normalized;

      try {
        const imageUrl = new URL(normalized);
        imageUrl.searchParams.set("width", String(width));
        return imageUrl.href;
      } catch {
        return normalized;
      }
    }

    buildShopifyImageSrcset(url, widths) {
      const normalized = this.normalizeImageUrl(url);
      if (!normalized || !Array.isArray(widths) || widths.length === 0)
        return "";

      try {
        const baseUrl = new URL(normalized);
        return widths
          .map((width) => {
            const sizedUrl = new URL(baseUrl.href);
            sizedUrl.searchParams.set("width", String(width));
            return sizedUrl.href + " " + width + "w";
          })
          .join(", ");
      } catch {
        return "";
      }
    }

    async loadProduct(handle) {
      if (!handle) return null;

      if (Object.prototype.hasOwnProperty.call(this.productCache, handle)) {
        return this.productCache[handle];
      }

      if (this.productLoads[handle]) {
        return this.productLoads[handle];
      }

      this.productLoads[handle] = (async () => {
        try {
          const response = await fetch(this.buildProductUrl(handle), {
            credentials: "same-origin",
          });

          if (!response.ok) {
            this.productCache[handle] = null;
            return null;
          }

          const product = await response.json();
          this.productCache[handle] = product;
          return product;
        } catch {
          this.productCache[handle] = null;
          return null;
        } finally {
          delete this.productLoads[handle];
        }
      })();

      return this.productLoads[handle];
    }

    extractImageUrl(value) {
      if (!value) return "";
      if (typeof value === "string") return this.normalizeImageUrl(value);
      if (typeof value === "object" && value.src)
        return this.normalizeImageUrl(value.src);
      return "";
    }

    getFeaturedImageUrl(product) {
      if (!product) return "";

      const directCandidates = [
        product.featured_image,
        product.featured_media?.preview_image,
        product.featured_media,
        product.image,
      ];

      for (const candidate of directCandidates) {
        const url = this.extractImageUrl(candidate);
        if (url) return url;
      }

      if (Array.isArray(product.images)) {
        for (const image of product.images) {
          const url = this.extractImageUrl(image);
          if (url) return url;
        }
      }

      if (Array.isArray(product.media)) {
        for (const item of product.media) {
          const url =
            this.extractImageUrl(item.preview_image) ||
            (item.media_type === "image" ? this.extractImageUrl(item) : "");
          if (url) return url;
        }
      }

      if (Array.isArray(product.variants)) {
        for (const variant of product.variants) {
          const url = this.extractImageUrl(variant.featured_image);
          if (url) return url;

          const position = variant.featured_image?.position;
          if (
            position &&
            Array.isArray(product.images) &&
            product.images[position - 1]
          ) {
            const imageFromPosition = this.extractImageUrl(
              product.images[position - 1],
            );
            if (imageFromPosition) return imageFromPosition;
          }
        }
      }

      return "";
    }

    async prefetchProductsForReviews(reviews) {
      const handles = [
        ...new Set(
          reviews
            .map((review) => review.productHandle)
            .filter(
              (handle) =>
                handle && this.hasProductBlock({ productHandle: handle }),
            ),
        ),
      ];

      await Promise.all(handles.map((handle) => this.loadProduct(handle)));
    }

    formatSourceLabel(source) {
      if (source === "etsy") return "Etsy";
      if (source === "google") return "Google";
      return "Website";
    }

    appendProductBlock(footer, review) {
      if (!this.hasProductBlock(review)) return;

      const productUrl = this.buildProductPageUrl(review.productHandle);
      const productTitle = review.productTitle || review.productHandle;
      const productLink = document.createElement("a");
      productLink.className = "reviews-list__product";
      productLink.href = productUrl;

      const imageUrl = this.getFeaturedImageUrl(
        this.productCache[review.productHandle],
      );
      if (imageUrl) {
        const maxWidth =
          this.productImageWidths[this.productImageWidths.length - 1];
        const sizedUrl = this.resizeShopifyImageUrl(imageUrl, maxWidth);
        const srcset = this.buildShopifyImageSrcset(
          imageUrl,
          this.productImageWidths,
        );

        const media = document.createElement("span");
        media.className = "reviews-list__product-media";

        const img = document.createElement("img");
        img.className = "reviews-list__product-image";
        img.src = sizedUrl;
        if (srcset) {
          img.srcset = srcset;
          img.sizes = "(min-width: 1000px) 3.5rem, 3.25rem";
        }
        img.alt = productTitle;
        img.loading = "lazy";
        img.width = maxWidth;
        img.height = maxWidth;

        media.appendChild(img);
        productLink.appendChild(media);
      }

      const title = document.createElement("span");
      title.className = "reviews-list__product-title";
      title.textContent = productTitle;
      productLink.appendChild(title);

      footer.appendChild(productLink);
    }

    isVerifiedReview(review) {
      if (!review) return false;
      if (review.verified === true) return true;
      if (review.source === "etsy") return true;
      return false;
    }

    formatReviewerName(name) {
      const value = String(name || "").trim();
      if (!value || value.toLowerCase() === "anonymous") return "Customer";
      return value;
    }

    normalizeReviewRecord(review) {
      review.name = this.formatReviewerName(review.name);
      return review;
    }

    buildCardElement(review) {
      const card = document.createElement("article");
      card.className = "reviews-list__card";

      const header = document.createElement("div");
      header.className = "reviews-list__header";

      const starsRow = document.createElement("div");
      starsRow.className = "reviews-list__stars-row";
      starsRow.innerHTML = this.buildStarsHtml(review.rating);
      header.appendChild(starsRow);

      const sourceLabel = this.formatSourceLabel(review.source);
      if (sourceLabel) {
        const source = document.createElement("span");
        source.className = "reviews-list__source";
        source.textContent = sourceLabel;
        header.appendChild(source);
      }

      card.appendChild(header);

      if (review.body) {
        const quote = document.createElement("blockquote");
        quote.className = "reviews-list__quote";
        quote.textContent = review.body;
        card.appendChild(quote);
      }

      const footer = document.createElement("div");
      footer.className = "reviews-list__footer";

      const authorMeta = document.createElement("div");
      authorMeta.className = "reviews-list__author-meta";

      if (this.isVerifiedReview(review) && this.verifiedBadgeHtml) {
        authorMeta.insertAdjacentHTML("beforeend", this.verifiedBadgeHtml);
      }

      if (review.name) {
        const author = document.createElement("cite");
        author.className = "reviews-list__author";
        author.textContent = review.name;
        authorMeta.appendChild(author);
      }

      footer.appendChild(authorMeta);
      this.appendProductBlock(footer, review);
      card.appendChild(footer);

      const divider = document.createElement("div");
      divider.className = "reviews-list__divider";
      divider.setAttribute("aria-hidden", "true");
      card.appendChild(divider);

      return card;
    }

    isShopReview(review) {
      return review.productHandle === "judgeme-shop-reviews";
    }

    hasProductBlock(review) {
      return Boolean(review.productHandle) && !this.isShopReview(review);
    }

    isDisplayableReview(review) {
      return Boolean(review);
    }

    buildStarsHtml(rating) {
      const value = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
      const aria = value + " out of 5 stars";
      let stars = "";

      for (let i = 1; i <= 5; i += 1) {
        const filledClass = i <= value ? " reviews-list__star--filled" : "";
        stars +=
          '<span class="reviews-list__star' +
          filledClass +
          '">' +
          this.starIconHtml +
          "</span>";
      }

      return (
        '<span class="reviews-list__stars" role="img" aria-label="' +
        this.escapeHtml(aria) +
        '">' +
        stars +
        "</span>"
      );
    }

    escapeHtml(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    async renderCards(reviews, generation) {
      if (!this.gridEl) return;

      if (reviews.length === 0) {
        if (generation !== this.renderGeneration) return;
        this.gridEl.hidden = true;
        this.gridEl.replaceChildren();
        return;
      }

      await this.prefetchProductsForReviews(reviews);

      if (generation !== this.renderGeneration) return;

      if (this.emptyEl) this.emptyEl.hidden = true;
      this.gridEl.replaceChildren(
        ...reviews.map((review) => this.buildCardElement(review)),
      );
      this.gridEl.hidden = false;
    }

    getFilterCounts() {
      return this.countReviewsByFacet(this.getDisplayableReviews());
    }

    isProductFilterNarrowed(activeProducts = this.activeProducts) {
      const totalProducts =
        this.knownProductHandles?.size ||
        Object.keys(this.getFilterCounts().products).length;
      return activeProducts.size > 0 && activeProducts.size < totalProducts;
    }

    reviewMatchesFilters(review, skipFilter) {
      if (
        skipFilter !== "source" &&
        this.activeSources.size > 0 &&
        !this.activeSources.has(review.source)
      ) {
        return false;
      }

      if (
        skipFilter !== "rating" &&
        this.activeRatings.size > 0 &&
        !this.activeRatings.has(Number(review.rating))
      ) {
        return false;
      }

      if (skipFilter === "product") return true;

      if (this.activeProducts.size === 0) {
        if (this.hasProductBlock(review)) return false;
        return true;
      }

      if (this.isProductFilterNarrowed()) {
        if (!this.hasProductBlock(review)) return false;
        if (!this.activeProducts.has(review.productHandle)) return false;
      } else if (
        this.activeProducts.size > 0 &&
        this.hasProductBlock(review) &&
        !this.activeProducts.has(review.productHandle)
      ) {
        return false;
      }

      return true;
    }

    countReviewsByFacet(reviews) {
      const sources = {};
      const ratings = {};
      const products = {};

      for (const review of reviews) {
        sources[review.source] = (sources[review.source] || 0) + 1;
        const rating = Number(review.rating);
        if (rating) ratings[rating] = (ratings[rating] || 0) + 1;

        if (this.hasProductBlock(review)) {
          if (!products[review.productHandle]) {
            products[review.productHandle] = {
              count: 0,
              title: review.productTitle || review.productHandle,
            };
          }
          products[review.productHandle].count += 1;
        }
      }

      return { sources, ratings, products };
    }

    getFacetCounts(skipFilter) {
      const matching = this.getDisplayableReviews().filter((review) =>
        this.reviewMatchesFilters(review, skipFilter),
      );
      return this.countReviewsByFacet(matching);
    }

    syncFilterStateFromCatalog() {
      const counts = this.getFilterCounts();
      const sourceKeys = Object.keys(counts.sources);
      const ratingKeys = Object.keys(counts.ratings).map((value) =>
        parseInt(value, 10),
      );
      const productKeys = Object.keys(counts.products);

      if (!this.filtersInitialized) {
        this.activeSources = new Set(sourceKeys);
        this.activeRatings = new Set(ratingKeys);
        this.activeProducts = new Set(productKeys);
        this.knownSourceKeys = new Set(sourceKeys);
        this.knownRatings = new Set(ratingKeys);
        this.knownProductHandles = new Set(productKeys);
        this.visibleCount = this.initialCount;
        this.filtersInitialized = true;
        return;
      }

      for (const source of sourceKeys) {
        if (!this.knownSourceKeys.has(source)) {
          this.activeSources.add(source);
          this.knownSourceKeys.add(source);
        }
      }

      for (const rating of ratingKeys) {
        if (!this.knownRatings.has(rating)) {
          this.activeRatings.add(rating);
          this.knownRatings.add(rating);
        }
      }

      for (const handle of productKeys) {
        if (!this.knownProductHandles.has(handle)) {
          this.activeProducts.add(handle);
          this.knownProductHandles.add(handle);
        }
      }
    }

    formatFilterSourceLabel(source) {
      if (source === "etsy") return "Etsy";
      if (source === "google") return "Google Business Reviews";
      return "Website";
    }

    buildFilterButton(type, value, label, count, isActive) {
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "reviews-list__filter-btn";
      button.setAttribute("data-reviews-filter", type);
      button.setAttribute("data-filter-value", String(value));
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
      button.innerHTML =
        '<span class="reviews-list__filter-indicator" aria-hidden="true">' +
        this.filterCheckIconHtml +
        "</span>" +
        '<span class="reviews-list__filter-label">' +
        this.escapeHtml(label) +
        "</span>" +
        '<span class="reviews-list__filter-count">' +
        count +
        "</span>";
      item.appendChild(button);
      return item;
    }

    buildSelectAllButton(type, legend) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "reviews-list__filter-select-all";
      button.setAttribute("data-reviews-filter-select-all", type);
      button.textContent = "Select all";
      button.setAttribute("aria-label", "Select all " + legend + " filters");
      return button;
    }

    buildFilterGroup(type, legend, optionsList, optionsClass) {
      const fieldset = document.createElement("fieldset");
      fieldset.className = "reviews-list__filter-group";
      const headingId = "reviews-filter-heading-" + type;
      fieldset.setAttribute("aria-labelledby", headingId);

      const headingDesktop = document.createElement("div");
      headingDesktop.className =
        "reviews-list__filter-heading reviews-list__filter-heading--desktop";

      const legendDesktop = document.createElement("span");
      legendDesktop.id = headingId;
      legendDesktop.className = "reviews-list__filter-legend";
      legendDesktop.textContent = legend;

      headingDesktop.appendChild(legendDesktop);
      headingDesktop.appendChild(this.buildSelectAllButton(type, legend));
      fieldset.appendChild(headingDesktop);

      const details = document.createElement("details");
      details.className = "reviews-list__filter-accordion";
      details.setAttribute("data-reviews-filter-accordion", type);

      const summary = document.createElement("summary");
      summary.className = "reviews-list__filter-summary";

      const legendMobile = document.createElement("span");
      legendMobile.className = "reviews-list__filter-legend";
      legendMobile.textContent = legend;
      legendMobile.setAttribute("aria-hidden", "true");

      const summaryEnd = document.createElement("span");
      summaryEnd.className = "reviews-list__filter-summary-end";
      if (this.filterChevronHtml) {
        summaryEnd.insertAdjacentHTML("beforeend", this.filterChevronHtml);
      }

      summary.appendChild(legendMobile);
      summary.appendChild(summaryEnd);
      details.appendChild(summary);

      const panel = document.createElement("div");
      panel.className = "reviews-list__filter-panel";

      const headingMobile = document.createElement("div");
      headingMobile.className =
        "reviews-list__filter-heading reviews-list__filter-heading--mobile";
      headingMobile.appendChild(this.buildSelectAllButton(type, legend));
      panel.appendChild(headingMobile);

      const list = document.createElement("ul");
      list.className = "reviews-list__filter-options";
      if (optionsClass) list.classList.add(optionsClass);

      for (const option of optionsList) {
        list.appendChild(option);
      }

      if (optionsClass === "reviews-list__filter-options--products") {
        const scrollWrap = document.createElement("div");
        scrollWrap.className = "reviews-list__filter-options-scroll";
        scrollWrap.appendChild(list);
        panel.appendChild(scrollWrap);
        this.initProductFilterScroll(scrollWrap, list);
      } else {
        panel.appendChild(list);
      }

      details.appendChild(panel);
      fieldset.appendChild(details);

      return fieldset;
    }

    syncFilterAccordionState() {
      if (!this.filtersEl) return;

      const accordions = this.filtersEl.querySelectorAll(
        "[data-reviews-filter-accordion]",
      );

      for (const details of accordions) {
        if (this.filterAccordionMedia.matches) {
          details.open = true;
        } else {
          details.open =
            details.getAttribute("data-reviews-filter-accordion") === "source";
        }
      }
    }

    initProductFilterScroll(scrollWrap, listEl) {
      const minVisibleItems = 3;

      const syncScrollState = () => {
        listEl.style.maxHeight = "none";
        listEl.style.overflow = "visible";

        const fullHeight = listEl.scrollHeight;
        const firstRow = listEl.querySelector("li");
        const rowHeight = firstRow
          ? firstRow.getBoundingClientRect().height
          : 44;
        const minHeight = rowHeight * minVisibleItems;
        const halfHeight = Math.max(Math.ceil(fullHeight / 2), minHeight);

        listEl.style.maxHeight = halfHeight + "px";
        listEl.style.overflowY = "auto";

        const canScroll = listEl.scrollHeight > listEl.clientHeight + 1;
        const atBottom =
          listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - 1;
        scrollWrap.toggleAttribute("data-scrollable", canScroll && !atBottom);
      };

      listEl.addEventListener("scroll", syncScrollState, { passive: true });

      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(syncScrollState);
        observer.observe(listEl);
      }

      requestAnimationFrame(syncScrollState);
    }

    buildFilterUI() {
      if (!this.filtersEl) return;

      const catalogCounts = this.getFilterCounts();
      const sourceCounts = this.getFacetCounts("source");
      const ratingCounts = this.getFacetCounts("rating");
      const container = document.createElement("div");
      container.className = "reviews-list__filters";

      const sourceOrder = ["etsy", "google", "general"];
      const sourceOptions = sourceOrder
        .filter((source) => catalogCounts.sources[source])
        .map((source) =>
          this.buildFilterButton(
            "source",
            source,
            this.formatFilterSourceLabel(source),
            sourceCounts.sources[source] || 0,
            this.activeSources.has(source),
          ),
        );

      if (sourceOptions.length > 0) {
        container.appendChild(
          this.buildFilterGroup("source", "Source", sourceOptions),
        );
      }

      const ratingOptions = [5, 4, 3, 2, 1]
        .filter((rating) => catalogCounts.ratings[rating])
        .map((rating) =>
          this.buildFilterButton(
            "rating",
            rating,
            rating + " stars",
            ratingCounts.ratings[rating] || 0,
            this.activeRatings.has(rating),
          ),
        );

      if (ratingOptions.length > 0) {
        container.appendChild(
          this.buildFilterGroup("rating", "Rating", ratingOptions),
        );
      }

      this.filtersEl.replaceChildren(container);
      this.filtersEl.hidden = container.children.length === 0;
      this.syncFilterAccordionState();
      this.syncFilterSelectAllButtons();
    }

    isFilterGroupFullySelected(fieldset) {
      const buttons = fieldset.querySelectorAll("[data-reviews-filter]");
      if (buttons.length === 0) return false;

      return Array.from(buttons).every(
        (button) => button.getAttribute("aria-pressed") === "true",
      );
    }

    syncFilterSelectAllButtons() {
      if (!this.filtersEl) return;

      const toggleButtons = this.filtersEl.querySelectorAll(
        "[data-reviews-filter-select-all]",
      );

      for (const button of toggleButtons) {
        const fieldset = button.closest(".reviews-list__filter-group");
        if (!fieldset) continue;

        const allSelected = this.isFilterGroupFullySelected(fieldset);
        const legend =
          fieldset
            .querySelector(".reviews-list__filter-legend")
            ?.textContent?.trim() || "";

        button.textContent = allSelected ? "Deselect all" : "Select all";
        button.setAttribute(
          "aria-label",
          (allSelected ? "Deselect all " : "Select all ") + legend + " filters",
        );
      }
    }

    updateFilterCounts() {
      if (!this.filtersEl) return;

      const sourceCounts = this.getFacetCounts("source");
      const productCounts = this.getFacetCounts("product");
      const ratingCounts = this.getFacetCounts("rating");

      for (const button of this.filtersEl.querySelectorAll(
        "[data-reviews-filter]",
      )) {
        const type = button.getAttribute("data-reviews-filter");
        const value = button.getAttribute("data-filter-value");
        const countEl = button.querySelector(".reviews-list__filter-count");
        if (!countEl) continue;

        if (type === "source") {
          countEl.textContent = String(sourceCounts.sources[value] || 0);
        } else if (type === "rating") {
          countEl.textContent = String(
            ratingCounts.ratings[parseInt(value, 10)] || 0,
          );
        } else if (type === "product") {
          countEl.textContent = String(
            productCounts.products[value]?.count || 0,
          );
        }
      }
    }

    onFilterClick(event) {
      const selectAllBtn = event.target.closest(
        "[data-reviews-filter-select-all]",
      );
      if (selectAllBtn && this.filtersEl?.contains(selectAllBtn)) {
        this.onFilterSelectAll(selectAllBtn);
        return;
      }

      const button = event.target.closest("[data-reviews-filter]");
      if (!button || !this.filtersEl?.contains(button)) return;

      const type = button.getAttribute("data-reviews-filter");
      const value = button.getAttribute("data-filter-value");
      const isActive = button.getAttribute("aria-pressed") === "true";

      if (type === "source") {
        if (isActive) this.activeSources.delete(value);
        else this.activeSources.add(value);
      } else if (type === "rating") {
        const rating = parseInt(value, 10);
        if (isActive) this.activeRatings.delete(rating);
        else this.activeRatings.add(rating);
      } else if (type === "product") {
        if (isActive) {
          if (this.activeProducts.size <= 1) return;
          this.activeProducts.delete(value);
        } else {
          this.activeProducts.add(value);
        }
      } else {
        return;
      }

      button.setAttribute("aria-pressed", isActive ? "false" : "true");
      this.syncFilterSelectAllButtons();
      this.visibleCount = this.initialCount;
      this.applyFilters();
      this.updateFilterCounts();
    }

    onFilterSelectAll(button) {
      const type = button.getAttribute("data-reviews-filter-select-all");
      const fieldset = button.closest(".reviews-list__filter-group");
      if (!type || !fieldset) return;

      const filterButtons = fieldset.querySelectorAll("[data-reviews-filter]");
      const selectAll = !this.isFilterGroupFullySelected(fieldset);

      for (const filterButton of filterButtons) {
        const value = filterButton.getAttribute("data-filter-value");
        filterButton.setAttribute("aria-pressed", selectAll ? "true" : "false");

        if (type === "source") {
          if (selectAll) this.activeSources.add(value);
          else this.activeSources.delete(value);
        } else if (type === "rating") {
          const rating = parseInt(value, 10);
          if (selectAll) this.activeRatings.add(rating);
          else this.activeRatings.delete(rating);
        } else if (type === "product") {
          if (selectAll) this.activeProducts.add(value);
          else this.activeProducts.delete(value);
        }
      }

      this.syncFilterSelectAllButtons();
      this.visibleCount = this.initialCount;
      this.applyFilters();
      this.updateFilterCounts();
    }

    updateLoadMoreButton(filteredLength) {
      if (!this.loadMoreWrapEl) return;
      this.loadMoreWrapEl.hidden = filteredLength <= this.visibleCount;
    }

    async onLoadMoreClick(event) {
      if (
        !event.target.closest("button") ||
        event.target.closest("button")?.disabled
      )
        return;

      const filtered = this.getFilteredReviews();
      if (filtered.length <= this.visibleCount) {
        this.updateLoadMoreButton(filtered.length);
        return;
      }

      this.visibleCount += this.loadMoreCount;
      const generation = this.renderGeneration;
      await this.renderCards(filtered.slice(0, this.visibleCount), generation);

      if (generation !== this.renderGeneration) return;

      this.updateLoadMoreButton(filtered.length);
    }

    getFilteredReviews() {
      return this.getDisplayableReviews().filter((review) =>
        this.reviewMatchesFilters(review),
      );
    }

    async applyFilters() {
      const generation = ++this.renderGeneration;
      const filtered = this.getFilteredReviews();
      this.visibleCount = this.initialCount;

      if (filtered.length === 0) {
        if (generation !== this.renderGeneration) return;

        if (this.gridEl) {
          this.gridEl.hidden = true;
          this.gridEl.replaceChildren();
        }
        if (this.emptyEl) this.emptyEl.hidden = false;
        if (this.statusEl) this.statusEl.hidden = true;
        if (this.loadMoreWrapEl) this.loadMoreWrapEl.hidden = true;
        return;
      }

      await this.renderCards(filtered.slice(0, this.visibleCount), generation);

      if (generation !== this.renderGeneration) return;

      this.updateLoadMoreButton(filtered.length);
    }

    getDisplayableReviews() {
      return this.allReviews;
    }

    ingestReviews(reviews) {
      this.allReviews = [];
      this.seenReviewIds.clear();

      for (const review of reviews) {
        if (this.seenReviewIds.has(review.id)) continue;
        this.seenReviewIds.add(review.id);
        this.allReviews.push(this.normalizeReviewRecord(review));
      }
    }

    updateHeroReviewCount(count) {
      if (window.RelicStoreReviewCount?.update) {
        window.RelicStoreReviewCount.update(count);
        return;
      }

      const countEls = document.querySelectorAll("[data-store-review-count]");

      for (const el of countEls) {
        el.textContent = String(count);
      }
    }

    mergeReviews(batch) {
      for (const review of batch) {
        if (this.seenReviewIds.has(review.id)) continue;
        this.seenReviewIds.add(review.id);
        this.allReviews.push(this.normalizeReviewRecord(review));
      }
    }

    getReviewsCacheKey() {
      const shop = window.Shopify?.shop || window.location.hostname;
      return REVIEWS_CACHE_KEY_PREFIX + shop;
    }

    readReviewsCache() {
      try {
        const raw = sessionStorage.getItem(this.getReviewsCacheKey());
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (
          parsed?.version !== REVIEWS_CACHE_VERSION ||
          !Array.isArray(parsed.reviews)
        ) {
          return null;
        }

        return parsed;
      } catch {
        return null;
      }
    }

    writeReviewsCache(reviews, total) {
      try {
        sessionStorage.setItem(
          this.getReviewsCacheKey(),
          JSON.stringify({
            version: REVIEWS_CACHE_VERSION,
            reviews,
            total,
            cachedAt: Date.now(),
          }),
        );
      } catch {
        // Storage blocked, full, or unavailable — fetch still succeeded.
      }
    }

    async fetchReviewsPage(page) {
      const perPage = 100;
      const url = new URL(
        "/apps/relic-modern-reviews-proxy/reviews",
        window.location.origin,
      );
      url.searchParams.set("page", String(page));
      url.searchParams.set("per_page", String(perPage));

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(
          "Could not load reviews (HTTP " + response.status + ").",
        );
      }

      const data = await response.json();
      return {
        batch: data.reviews || [],
        hasMore: (data.reviews || []).length === perPage,
      };
    }

    async fetchAllReviewsPaginated() {
      this.allReviews = [];
      this.seenReviewIds.clear();

      let page = 1;
      let keepGoing = true;

      while (keepGoing && page <= 50) {
        const pageNumbers = [];
        for (let p = page; p < page + 5 && p <= 50; p += 1) {
          pageNumbers.push(p);
        }

        const results = await Promise.all(
          pageNumbers.map((p) => this.fetchReviewsPage(p)),
        );

        for (const result of results) {
          this.mergeReviews(result.batch);
          if (result.batch.length < 100) {
            keepGoing = false;
          }
        }

        page += pageNumbers.length;
      }
    }

    async fetchAllReviews() {
      const cached = this.readReviewsCache();
      if (cached) {
        this.ingestReviews(cached.reviews);
        this.reviewsFullyLoaded = true;
        return;
      }

      const url = new URL(
        "/apps/relic-modern-reviews-proxy/reviews",
        window.location.origin,
      );
      url.searchParams.set("all", "1");

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(
          "Could not load reviews (HTTP " + response.status + ").",
        );
      }

      const data = await response.json();

      if (typeof data.total === "number" && Array.isArray(data.reviews)) {
        this.ingestReviews(data.reviews);
        this.reviewsFullyLoaded = true;
        this.writeReviewsCache(this.allReviews, data.total);
        return;
      }

      await this.fetchAllReviewsPaginated();
      this.reviewsFullyLoaded = true;
      this.writeReviewsCache(this.allReviews, this.allReviews.length);
    }

    async initReviews() {
      const cached = this.readReviewsCache();

      if (cached) {
        this.hideLoadingStatus();
      } else {
        this.showLoading();
      }

      try {
        await this.fetchAllReviews();

        const displayable = this.getDisplayableReviews();
        this.updateHeroReviewCount(displayable.length);

        if (displayable.length === 0) {
          this.setStatus("No reviews yet.");
          return;
        }

        this.syncFilterStateFromCatalog();
        await this.applyFilters();
        this.hideLoadingStatus();
        this.buildFilterUI();

        this.dispatchEvent(
          new CustomEvent("relic-reviews:loaded", {
            bubbles: true,
            detail: { count: this.allReviews.length },
          }),
        );
      } catch (error) {
        this.setStatus(error.message || "Failed to load reviews.", true);
      }
    }

    showLoading() {
      if (!this.statusEl) return;

      const textEl = this.statusEl.querySelector("[data-reviews-status-text]");
      this.statusEl.hidden = false;
      this.statusEl.setAttribute("data-reviews-loading", "");
      this.statusEl.setAttribute("aria-busy", "true");
      this.statusEl.setAttribute("aria-label", "Loading reviews");
      this.statusEl.removeAttribute("data-reviews-error");

      if (this.innerEl) this.innerEl.setAttribute("data-reviews-loading", "");

      if (textEl) textEl.hidden = true;
    }

    hideLoadingStatus() {
      if (this.statusEl) {
        this.statusEl.hidden = true;
        this.statusEl.removeAttribute("data-reviews-loading");
        this.statusEl.removeAttribute("aria-label");
        this.statusEl.setAttribute("aria-busy", "false");
      }

      if (this.innerEl) this.innerEl.removeAttribute("data-reviews-loading");
    }

    setStatus(message, isError) {
      if (!this.statusEl) return;

      const textEl = this.statusEl.querySelector("[data-reviews-status-text]");
      this.statusEl.hidden = false;
      this.statusEl.removeAttribute("data-reviews-loading");
      this.statusEl.removeAttribute("aria-label");
      this.statusEl.setAttribute("aria-busy", "false");

      if (this.innerEl) this.innerEl.removeAttribute("data-reviews-loading");

      if (textEl) {
        textEl.hidden = false;
        textEl.textContent = message;
      } else {
        this.statusEl.textContent = message;
      }

      if (isError) {
        this.statusEl.setAttribute("data-reviews-error", "");
      } else {
        this.statusEl.removeAttribute("data-reviews-error");
      }
    }
  }

  customElements.define("relic-reviews-page", RelicReviewsPage);
}
